import { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Bug, Plus, Trash2, PencilLine, Wrench, CheckCircle2, XCircle, History, RotateCcw } from "lucide-react";
import { useConfirm } from "../ConfirmProvider";
import { BugMediaPicker, BugMediaGallery } from "./BugMedia";

const BUG_STATUSES = ["Open", "In Progress", "Ready for Retest", "Reopened", "Closed"];
const SEVERITIES = ["Low", "Medium", "High", "Critical"];
const FIXABLE = ["Open", "In Progress", "Reopened"];

const SEV_STYLES = {
  "Low": "bg-neutral-100 text-neutral-700 border-neutral-300",
  "Medium": "bg-blue-50 text-blue-700 border-blue-300",
  "High": "bg-amber-50 text-amber-700 border-amber-300",
  "Critical": "bg-rose-50 text-rose-700 border-rose-300",
};
const STATUS_STYLES = {
  "Open": "bg-rose-50 text-rose-700 border-rose-300",
  "In Progress": "bg-blue-50 text-blue-700 border-blue-300",
  "Ready for Retest": "bg-violet-50 text-violet-700 border-violet-300",
  "Resolved": "bg-emerald-50 text-emerald-700 border-emerald-300",
  "Closed": "bg-emerald-50 text-emerald-700 border-emerald-300",
  "Reopened": "bg-orange-50 text-orange-700 border-orange-300",
};

const EMPTY = { title: "", description: "", severity: "Medium", status: "Open", assigned_to: "", steps_to_reproduce: "", milestone_id: "", attachments: [] };

export default function BugsPanel({ projectId, users = [], milestones = [], currentUser, onChanged }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [failing, setFailing] = useState(null);
  const [filter, setFilter] = useState("All");
  const confirm = useConfirm();

  const load = async () => {
    try { const { data } = await api.get(`/projects/${projectId}/bugs`); setItems(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [projectId]);

  const me = currentUser?.id;
  const canEditAll = ["admin", "ceo", "coo", "manager", "project_manager"].includes(currentUser?.role);
  const perms = {
    nameOf: (uid) => users.find(u => u.id === uid)?.name || "Unassigned",
    msName: (mid) => milestones.find(m => m.id === mid)?.name || "",
    canEdit: (b) => canEditAll || b.reporter_id === me || (["developer", "tester"].includes(currentUser?.role) && b.assigned_to === me),
    canDelete: (b) => canEditAll || b.reporter_id === me,
    canStatus: (b) => canEditAll || b.reporter_id === me || b.assigned_to === me,
    canFix: (b) => FIXABLE.includes(b.status) && (canEditAll || b.assigned_to === me),
    canRetest: (b) => b.status === "Ready for Retest" && (canEditAll || b.reporter_id === me || currentUser?.role === "tester"),
    canClose: (b) => canEditAll || b.reporter_id === me || currentUser?.role === "tester",
  };

  const queues = useMemo(() => ({
    All: () => true,
    "Needs Fix": (b) => FIXABLE.includes(b.status),
    "Ready for Retest": (b) => b.status === "Ready for Retest",
    "My Retest Queue": (b) => perms.canRetest(b),
    "Assigned to Me": (b) => b.assigned_to === me && b.status !== "Closed",
    Closed: (b) => ["Closed", "Resolved"].includes(b.status),
  }), [items, me]);
  const filtered = items.filter(queues[filter]);

  const done = () => { load(); onChanged?.(); };
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); done(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const handlers = {
    edit: (b) => { setEditing(b); setOpen(true); },
    remove: async (b) => {
      if (!(await confirm({ title: "Are you sure you want to delete this bug?", description: `"${b.title}" will be permanently removed.` }))) return;
      act(() => api.delete(`/bugs/${b.id}`), "Bug deleted");
    },
    status: (b, status) => act(() => api.put(`/bugs/${b.id}`, { status }), `Moved to ${status}`),
    fix: (b) => act(() => api.put(`/bugs/${b.id}`, { status: "Ready for Retest" }), "Sent for retest"),
    pass: (b) => act(() => api.post(`/bugs/${b.id}/retest`, { passed: true }), "Retest passed, bug closed"),
    fail: (b) => setFailing(b),
  };

  return (
    <div className="space-y-4" data-testid="bugs-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-orange-400">Bug Board</div>
          <h3 className="text-lg font-bold">Bugs ({items.length})</h3>
        </div>
        <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="add-bug-btn">
          <Plus className="w-4 h-4 mr-1" /> Log Bug
        </Button>
      </div>
      <BugStats items={items} />
      <div className="flex flex-wrap gap-1.5">
        {Object.keys(queues).map(q => {
          const n = items.filter(queues[q]).length;
          return (
            <button key={q} onClick={() => setFilter(q)} data-testid={`bug-filter-${q.toLowerCase().replace(/\s+/g, "-")}`}
              className={`text-[10px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-full border transition-colors ${filter === q ? "bg-orange-100 text-orange-700 border-orange-300" : "bg-white text-neutral-600 border-neutral-200 hover:border-neutral-300"}`}>
              {q} {n > 0 && <span className="ml-1 opacity-70">{n}</span>}
            </button>
          );
        })}
      </div>
      {filtered.length === 0 && (
        <div className="panel p-8 text-center text-neutral-500 text-sm" data-testid="bugs-empty">
          <Bug className="w-6 h-6 mx-auto mb-2 opacity-50" />
          No bugs {filter !== "All" ? `in "${filter}"` : "yet"}.
        </div>
      )}
      <div className="space-y-2">
        {filtered.map(b => <BugCard key={b.id} b={b} perms={perms} on={handlers} />)}
      </div>
      <BugForm open={open} onOpenChange={setOpen} editing={editing} projectId={projectId} users={users} milestones={milestones}
        limited={editing && !canEditAll && editing.reporter_id !== me} onSaved={done} />
      <RetestFailDialog bug={failing} onClose={() => setFailing(null)} onDone={done} />
    </div>
  );
}

function BugStats({ items }) {
  const c = (fn) => items.filter(fn).length;
  const stats = [
    ["Needs fix", c(b => FIXABLE.includes(b.status)), "text-rose-700"],
    ["Awaiting retest", c(b => b.status === "Ready for Retest"), "text-violet-700"],
    ["Closed", c(b => ["Closed", "Resolved"].includes(b.status)), "text-emerald-700"],
    ["Failed retests", items.reduce((s, b) => s + (b.retest_fail_count || 0), 0), "text-orange-700"],
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
      {stats.map(([label, n, color]) => (
        <div key={label} className="panel p-3" data-testid={`bug-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
          <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">{label}</div>
          <div className={`text-2xl font-bold ${color}`}>{n}</div>
        </div>
      ))}
    </div>
  );
}

function BugCard({ b, perms, on }) {
  return (
    <div className="panel p-4" data-testid={`bug-${b.id}`}>
      <div className="flex items-start gap-3 flex-wrap sm:flex-nowrap">
        <div className="w-9 h-9 shrink-0 rounded-lg bg-rose-50 border border-rose-200 grid place-items-center"><Bug className="w-4 h-4 text-rose-600" /></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-mono text-neutral-500">{b.bug_id}</span>
            <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${SEV_STYLES[b.severity] || ""}`}>{b.severity}</span>
            <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${STATUS_STYLES[b.status] || ""}`} data-testid={`bug-status-pill-${b.id}`}>{b.status}</span>
            {b.retest_fail_count > 0 && <span className="text-[10px] font-mono px-2 py-0.5 rounded border border-orange-300 bg-orange-50 text-orange-700 flex items-center gap-1" data-testid={`bug-fails-${b.id}`}><RotateCcw className="w-3 h-3" />Failed retest ×{b.retest_fail_count}</span>}
            {b.milestone_id && <span className="text-[10px] font-mono text-neutral-600">◆ {perms.msName(b.milestone_id)}</span>}
          </div>
          <div className="font-semibold text-sm mt-1">{b.title}</div>
          {b.description && <div className="text-xs text-neutral-700 mt-1 whitespace-pre-wrap">{b.description}</div>}
          {b.attachments?.length > 0 && <div className="mt-2"><BugMediaGallery items={b.attachments} testid={`bug-media-${b.id}`} /></div>}
          {b.steps_to_reproduce && (
            <details className="mt-2">
              <summary className="text-[10px] font-mono uppercase tracking-wider text-neutral-500 cursor-pointer">Steps to Reproduce</summary>
              <div className="text-xs text-neutral-700 mt-1 whitespace-pre-wrap p-2 bg-neutral-50 rounded border border-neutral-200">{b.steps_to_reproduce}</div>
            </details>
          )}
          <div className="text-[10px] font-mono text-neutral-500 mt-2">
            Logged by {b.reporter_name || "—"} · Assigned to <span data-testid={`bug-assignee-${b.id}`}>{perms.nameOf(b.assigned_to)}</span>
            {b.verified_by_name && b.status === "Closed" && <> · Verified by {b.verified_by_name}</>}
          </div>
          <BugHistory b={b} />
        </div>
        <BugActions b={b} perms={perms} on={on} />
      </div>
    </div>
  );
}

function BugActions({ b, perms, on }) {
  return (
    <div className="flex items-center gap-1 flex-wrap justify-end w-full sm:w-auto">
      {perms.canFix(b) && <Button size="sm" variant="outline" onClick={() => on.fix(b)} className="border-violet-300 text-violet-700 hover:bg-violet-50" data-testid={`bug-fix-${b.id}`}><Wrench className="w-3.5 h-3.5 mr-1" />Mark Fixed</Button>}
      {perms.canRetest(b) && <>
        <Button size="sm" onClick={() => on.pass(b)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`bug-pass-${b.id}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Passed</Button>
        <Button size="sm" variant="outline" onClick={() => on.fail(b)} className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid={`bug-fail-${b.id}`}><XCircle className="w-3.5 h-3.5 mr-1" />Failed</Button>
      </>}
      {perms.canStatus(b) && (
        <Select value={b.status} onValueChange={(v) => on.status(b, v)}>
          <SelectTrigger className="h-8 w-36 text-xs bg-white border-neutral-200" data-testid={`bug-status-${b.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>{BUG_STATUSES.filter(s => s !== "Closed" || perms.canClose(b) || b.status === "Closed").map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      )}
      {perms.canEdit(b) && <Button variant="ghost" size="icon" onClick={() => on.edit(b)} data-testid={`bug-edit-${b.id}`} title="Edit bug"><PencilLine className="w-4 h-4" /></Button>}
      {perms.canDelete(b) && <Button title="Delete bug" variant="ghost" size="icon" onClick={() => on.remove(b)} className="text-rose-500 hover:text-rose-700" data-testid={`bug-delete-${b.id}`}><Trash2 className="w-4 h-4" /></Button>}
    </div>
  );
}

function BugHistory({ b }) {
  const h = [...(b.history || [])].reverse();
  if (!h.length) return null;
  return (
    <details className="mt-2" data-testid={`bug-history-${b.id}`}>
      <summary className="text-[10px] font-mono uppercase tracking-wider text-neutral-500 cursor-pointer flex items-center gap-1"><History className="w-3 h-3" />History ({h.length})</summary>
      <ol className="mt-2 space-y-1.5 border-l border-neutral-200 pl-3">
        {h.map(e => (
          <li key={e.id} className="text-xs text-neutral-700">
            <span className="font-medium">{e.user_name}</span> {e.action}
            <span className="text-[10px] font-mono text-neutral-400 ml-1">{new Date(e.at).toLocaleString()}</span>
            {e.note && <div className="mt-0.5 p-1.5 rounded bg-neutral-50 border border-neutral-200 whitespace-pre-wrap">{e.note}</div>}
            {e.attachments?.length > 0 && <div className="mt-1"><BugMediaGallery items={e.attachments} testid={`bug-event-media-${e.id}`} /></div>}
          </li>
        ))}
      </ol>
    </details>
  );
}

function RetestFailDialog({ bug, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [media, setMedia] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (bug) { setNote(""); setMedia([]); } }, [bug]);
  const submit = async () => {
    if (!note.trim()) { toast.error("Describe what still fails"); return; }
    setBusy(true);
    try { await api.post(`/bugs/${bug.id}/retest`, { passed: false, note, attachments: media }); toast.success("Bug reopened and sent back"); onClose(); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!bug} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white border-neutral-200 max-w-md" data-testid="retest-fail-dialog">
        <DialogHeader>
          <DialogTitle>Retest failed</DialogTitle>
          <DialogDescription className="text-xs">The bug goes back to {bug ? "the assignee" : ""} as Reopened with your note.</DialogDescription>
        </DialogHeader>
        <Textarea rows={4} value={note} onChange={e => setNote(e.target.value)} placeholder="What still fails? Steps, device, browser…" data-testid="retest-fail-note" />
        <BugMediaPicker value={media} onChange={setMedia} testid="retest-media" />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="retest-fail-submit">{busy ? "Saving..." : "Reopen Bug"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BugForm({ open, onOpenChange, editing, projectId, users, milestones, limited, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setForm(editing ? { ...EMPTY, ...editing } : EMPTY); } }, [open, editing]);
  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    try {
      const { title, description, severity, status, assigned_to, steps_to_reproduce, milestone_id, attachments } = form;
      const payload = { title, description, severity, status, assigned_to, steps_to_reproduce, milestone_id, attachments: attachments || [] };
      if (editing) await api.put(`/bugs/${editing.id}`, payload);
      else await api.post(`/projects/${projectId}/bugs`, payload);
      toast.success(editing ? "Bug updated" : "Bug logged");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const team = users.filter(u => ["developer", "tester", "project_manager"].includes(u.role) && !u.archived_at);
  const L = ({ children }) => <Label className="text-[10px] uppercase font-mono tracking-wider text-neutral-500">{children}</Label>;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-neutral-200 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="bug-dialog">
        <DialogHeader><DialogTitle>{editing ? "Edit Bug" : "Log a Bug"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><L>Title</L><Input value={form.title} disabled={limited} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="bug-title" /></div>
          <div className="space-y-1.5"><L>Description</L><Textarea rows={3} value={form.description} disabled={limited} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="bug-desc" /></div>
          <div className="space-y-1.5"><L>Steps to Reproduce</L><Textarea rows={3} value={form.steps_to_reproduce} onChange={e => setForm({ ...form, steps_to_reproduce: e.target.value })} data-testid="bug-steps" placeholder={"1. Do X\n2. Then Y\n3. Observe Z"} /></div>
          <div className="space-y-1.5"><L>Screenshots & Recordings</L><BugMediaPicker value={form.attachments || []} onChange={(v) => setForm(f => ({ ...f, attachments: v }))} testid="bug-form-media" /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><L>Severity</L>
              <Select value={form.severity} disabled={limited} onValueChange={v => setForm({ ...form, severity: v })}>
                <SelectTrigger data-testid="bug-severity"><SelectValue /></SelectTrigger>
                <SelectContent>{SEVERITIES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><L>Status</L>
              <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="bug-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{BUG_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><L>Assign To</L>
              <Select value={form.assigned_to || "__none"} disabled={limited} onValueChange={v => setForm({ ...form, assigned_to: v === "__none" ? "" : v })}>
                <SelectTrigger data-testid="bug-assignee"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">Unassigned</SelectItem>
                  {team.map(u => <SelectItem key={u.id} value={u.id}>{u.name} <span className="text-neutral-500 text-[10px] ml-1">({u.role.replace("_", " ")})</span></SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><L>Milestone</L>
              <Select value={form.milestone_id || "__none"} disabled={limited} onValueChange={v => setForm({ ...form, milestone_id: v === "__none" ? "" : v })}>
                <SelectTrigger data-testid="bug-milestone"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— None —</SelectItem>
                  {milestones.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy || !form.title.trim()} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="bug-save">{busy ? "Saving..." : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
